import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useApiData } from '../../api/client';
import { useAccessToken } from '../../auth/hooks';
import { exportGraphSubgraph } from '../../utils/exportGraph';
import { type DastarkhanProject } from '../../api/dastarkhan';
import { usePermissions } from '../../hooks/usePermissions';
import { useGraph } from '../../hooks/useGraph';
import { ProjectSettings } from './ProjectSettings';
import { ProjectMembers } from '../admin/ProjectMembers';
import { GraphCanvas, type ColorByAttribute, type GraphCanvasHandle } from '../graph/GraphCanvas';
import { GraphToolbar } from '../graph/GraphToolbar';
import { NodeDetailPanel } from '../graph/NodeDetailPanel';
import { EdgeDetailPanel } from '../graph/EdgeDetailPanel';
import { AuditLog } from '../audit/AuditLog';
import { CypherConsole } from '../cypher/CypherConsole';
import { AIExplorer } from '../explorer/AIExplorer';
import {
  LayoutPanel,
  type LayoutEngine,
  type FcoseParams,
  type D3ForceParams,
  type CoseBilkentParams,
  type SimpleParams,
  DEFAULT_FCOSE,
  DEFAULT_D3_FORCE,
  DEFAULT_COSE_BILKENT,
  DEFAULT_SIMPLE,
} from '../graph/LayoutPanel';
import { BackupWatermark } from './BackupWatermark';
import { InstructionsPanel } from './InstructionsPanel';
import type { GraphNode, GraphEdge } from '../../api/dastarkhan';
import {
  Tabs,
  Button,
  Card,
  Stack,
  Flex,
  useTheme,
} from '@fti/design-system';

interface ProjectDetailProps {
  projectId: string;
  onBack: () => void;
  onProjectInfo?: (info: { name: string; matterCode?: string }) => void;
}

export const ProjectDetail: React.FC<ProjectDetailProps> = ({ projectId, onBack, onProjectInfo }) => {
  const { theme } = useTheme();
  const { isAdmin, user, loadProjectMemberships } = usePermissions();
  const projectEndpoint = `/dastarkhan/projects/${projectId}`;
  const { data: project, loading, error: fetchError, refetch: fetchProject } = useApiData<DastarkhanProject>(projectEndpoint);
  const token = useAccessToken();
  const [activeTab, setActiveTab] = useState('graph');
  const [colorBy, setColorBy] = useState<ColorByAttribute>('_entityType');
  const [detailPanelEditing, setDetailPanelEditing] = useState(false);
  const [edgeFilterKey, setEdgeFilterKey] = useState<string | null>(null);
  const [edgeFilterValue, setEdgeFilterValue] = useState('');
  const [selectedNodeCount, setSelectedNodeCount] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showAIExplorer, setShowAIExplorer] = useState(false);
  const [showCypherConsole, setShowCypherConsole] = useState(false);
  const [showLayoutPanel, setShowLayoutPanel] = useState(false);
  const [layoutEngine, setLayoutEngine] = useState<LayoutEngine>('d3-force-worker');
  const [fcoseParams, setFcoseParams] = useState<FcoseParams>(DEFAULT_FCOSE);
  const [d3Params, setD3Params] = useState<D3ForceParams>(DEFAULT_D3_FORCE);
  const [coseBilkentParams, setCoseBilkentParams] = useState<CoseBilkentParams>(DEFAULT_COSE_BILKENT);
  const [simpleParams, setSimpleParams] = useState<SimpleParams>(DEFAULT_SIMPLE);
  const [layoutRunning, setLayoutRunning] = useState(false);
  const [cypherSubgraphNodes, setCypherSubgraphNodes] = useState<GraphNode[]>([]);
  const [cypherSubgraphEdges, setCypherSubgraphEdges] = useState<GraphEdge[]>([]);
  const canvasRef = useRef<GraphCanvasHandle>(null);
  const graphContainerRef = useRef<HTMLDivElement>(null);

  const error = fetchError ? 'Failed to load project' : null;

  const graph = useGraph(projectId);

  const canEdit = isAdmin || (project != null && user != null && project.owner_id === user.id);

  const edgePropertyKeys = useMemo(() => {
    const keys = new Set<string>();
    graph.edges.forEach((e) => Object.keys(e.properties).forEach((k) => keys.add(k)));
    return Array.from(keys).sort();
  }, [graph.edges]);



  // Load project memberships for permission checks (shared via PermissionsContext)
  useEffect(() => {
    loadProjectMemberships(projectId);
  }, [projectId, loadProjectMemberships]);

  // Notify parent of project info for header nav
  useEffect(() => {
    if (project && onProjectInfo) {
      onProjectInfo({ name: project.name, matterCode: project.matter_code || undefined });
    }
  }, [project, onProjectInfo]);

  const handleNodeSelect = useCallback((elementId: string) => {
    const node = graph.nodes.find((n) => n.element_id === elementId);
    if (node) {
      graph.setSelectedNode(node);
      graph.setSelectedEdge(null);
    }
  }, [graph]);

  const handleEdgeSelect = useCallback((elementId: string) => {
    const edge = graph.edges.find((e) => e.element_id === elementId);
    if (edge) {
      graph.setSelectedEdge(edge);
      graph.setSelectedNode(null);
    }
  }, [graph]);

  const [exportError, setExportError] = useState<string | null>(null);

  const handleExport = useCallback(async (format: string) => {
    setExportError(null);
    if (!token) {
      setExportError('Not authenticated. Please sign in again.');
      return;
    }
    try {
      let filteredNodeIds: string[] | undefined;
      let filteredEdgeIds: string[] | undefined;
      // Precedence:
      // 1. Highlighted/selected nodes always win — whether the canvas is showing
      //    the main graph or a cypher subgraph, the user's selection is the scope.
      // 2. Otherwise, if a cypher subgraph is active, export the whole subgraph.
      // 3. Otherwise, fall back to edge-filter visibility (main graph case).
      const selectedIds = canvasRef.current?.getSelectedNodeIds() || [];
      if (selectedIds.length >= 1) {
        filteredNodeIds = selectedIds;
      } else if (cypherSubgraphNodes.length > 0) {
        filteredNodeIds = cypherSubgraphNodes.map((n) => n.element_id);
        filteredEdgeIds = cypherSubgraphEdges.map((e) => e.element_id);
      } else if (edgeFilterKey && edgeFilterValue && canvasRef.current) {
        const visible = canvasRef.current.getVisibleIds();
        filteredNodeIds = visible.nodeIds;
        filteredEdgeIds = visible.edgeIds;
      }
      await exportGraphSubgraph(token, projectId, format, filteredNodeIds, filteredEdgeIds);
    } catch (err) {
      setExportError(err instanceof Error ? err.message : `Export (${format}) failed`);
    }
  }, [token, projectId, edgeFilterKey, edgeFilterValue, cypherSubgraphNodes, cypherSubgraphEdges]);

  const handleSearchNodes = useCallback((query: string) => {
    canvasRef.current?.searchNodes(query);
  }, []);

  // Poll selected nodes for export button text
  useEffect(() => {
    const interval = setInterval(() => {
      const count = canvasRef.current?.getSelectedNodeIds().length || 0;
      setSelectedNodeCount((prev) => prev !== count ? count : prev);
    }, 500);
    return () => clearInterval(interval);
  }, []);

  // Fullscreen toggle
  const handleToggleFullscreen = useCallback(() => {
    const el = graphContainerRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.().catch(() => {
        // CSS fallback handled by isFullscreen state
        setIsFullscreen(true);
      });
    } else {
      document.exitFullscreen?.();
    }
  }, []);

  // Sync fullscreen state with browser
  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // Exit fullscreen when leaving graph tab
  useEffect(() => {
    if (activeTab !== 'graph' && isFullscreen) {
      document.exitFullscreen?.().catch(() => setIsFullscreen(false));
    }
  }, [activeTab, isFullscreen]);

  const buildLayoutOptions = useCallback((): Record<string, unknown> => {
    if (layoutEngine === 'fcose') {
      return {
        name: 'fcose',
        quality: 'default',
        animate: 'end',
        animationDuration: 800,
        nodeDimensionsIncludeLabels: true,
        randomize: fcoseParams.randomize,
        tile: fcoseParams.tile,
        nodeRepulsion: () => fcoseParams.nodeRepulsion,
        idealEdgeLength: () => fcoseParams.idealEdgeLength,
        edgeElasticity: () => fcoseParams.edgeElasticity,
        gravity: fcoseParams.gravity,
        gravityRange: fcoseParams.gravityRange,
        nestingFactor: fcoseParams.nestingFactor,
        nodeSeparation: fcoseParams.nodeSeparation,
        numIter: fcoseParams.numIter,
      };
    }
    if (layoutEngine === 'd3-force') {
      return {
        name: 'd3-force',
        animate: true,
        fixedAfterDragging: !d3Params.infinite,
        infinite: d3Params.infinite,
        linkId: (d: any) => d.id,
        linkDistance: d3Params.linkDistance,
        linkStrength: d3Params.linkStrength,
        manyBodyStrength: d3Params.manyBodyStrength,
        xStrength: d3Params.centerStrength,
        yStrength: d3Params.centerStrength,
        collideRadius: d3Params.collideRadius,
        collideStrength: d3Params.collideStrength,
        alpha: d3Params.alpha,
        alphaDecay: d3Params.alphaDecay,
        velocityDecay: d3Params.velocityDecay,
      };
    }
    if (layoutEngine === 'cose-bilkent') {
      return {
        name: 'cose-bilkent',
        animate: 'end',
        animationDuration: 800,
        nodeDimensionsIncludeLabels: true,
        randomize: coseBilkentParams.randomize,
        nodeRepulsion: coseBilkentParams.nodeRepulsion,
        idealEdgeLength: coseBilkentParams.idealEdgeLength,
        gravity: coseBilkentParams.gravity,
        gravityRange: coseBilkentParams.gravityRange,
        numIter: coseBilkentParams.numIter,
        tilingPaddingVertical: coseBilkentParams.tilingPadding,
        tilingPaddingHorizontal: coseBilkentParams.tilingPadding,
      };
    }
    if (layoutEngine === 'cose') {
      return {
        name: 'cose',
        animate: 'end',
        animationDuration: 800,
        nodeDimensionsIncludeLabels: true,
        randomize: false,
      };
    }
    // Simple layouts: circle / concentric / breadthfirst / grid
    const spacing = simpleParams.spacingFactor;
    if (layoutEngine === 'concentric') {
      return {
        name: 'concentric',
        animate: false,
        concentric: (n: any) => n.degree(),
        levelWidth: () => 2,
        minNodeSpacing: 10 * spacing,
        nodeDimensionsIncludeLabels: true,
      };
    }
    return {
      name: layoutEngine,
      animate: false,
      spacingFactor: spacing,
      nodeDimensionsIncludeLabels: true,
    };
  }, [layoutEngine, fcoseParams, d3Params, coseBilkentParams, simpleParams]);

  const handleApplyLayout = useCallback(() => {
    // Running flag flips to true via the canvas's onLayoutStart callback,
    // back to false via onLayoutStop. Event-driven so the button reflects
    // the true simulation state on any engine (worker included).
    if (layoutEngine === 'd3-force-worker') {
      canvasRef.current?.runWorkerLayout({ ...d3Params, maxTicks: 600 });
    } else {
      canvasRef.current?.runLayoutWithOptions(buildLayoutOptions());
    }
  }, [layoutEngine, d3Params, buildLayoutOptions]);

  const handleStopLayout = useCallback(() => {
    canvasRef.current?.stopLayout();
  }, []);

  const handleToggleLayoutRun = useCallback(() => {
    if (layoutRunning) {
      canvasRef.current?.stopLayout();
    } else if (layoutEngine === 'd3-force-worker') {
      canvasRef.current?.runWorkerLayout({ ...d3Params, maxTicks: 600 });
    } else {
      canvasRef.current?.runLayoutWithOptions(buildLayoutOptions());
    }
  }, [layoutRunning, layoutEngine, d3Params, buildLayoutOptions]);

  const handleResetLayoutParams = useCallback(() => {
    if (layoutEngine === 'fcose') setFcoseParams(DEFAULT_FCOSE);
    else if (layoutEngine === 'd3-force') setD3Params(DEFAULT_D3_FORCE);
    else if (layoutEngine === 'cose-bilkent') setCoseBilkentParams(DEFAULT_COSE_BILKENT);
    else setSimpleParams(DEFAULT_SIMPLE);
  }, [layoutEngine]);

  // Cypher subgraph → render on canvas
  const handleCypherGraphData = useCallback((nodes: GraphNode[], edges: GraphEdge[]) => {
    setCypherSubgraphNodes(nodes);
    setCypherSubgraphEdges(edges);
  }, []);

  const handleClearCypherGraphData = useCallback(() => {
    setCypherSubgraphNodes([]);
    setCypherSubgraphEdges([]);
  }, []);

  // Convert Cypher subgraph to cytoscape elements when active
  const cypherCytoscapeElements = useMemo(() => {
    if (cypherSubgraphNodes.length === 0) return null;
    return [
      ...cypherSubgraphNodes.map((n) => ({
        data: {
          ...n.properties,
          id: n.element_id,
          label: (n.properties.name as string) || n.labels[0] || 'Node',
          _entityType: n.labels[0] || 'Unknown',
          _jurisdiction: (n.properties.jurisdiction as string) || 'Unknown',
          _labels: n.labels,
          _element_id: n.element_id,
        },
        group: 'nodes' as const,
      })),
      ...cypherSubgraphEdges.map((e) => ({
        data: {
          ...e.properties,
          id: e.element_id,
          source: e.source_element_id,
          target: e.target_element_id,
          label: e.type,
          _type: e.type,
          _element_id: e.element_id,
        },
        group: 'edges' as const,
      })),
    ];
  }, [cypherSubgraphNodes, cypherSubgraphEdges]);

  if (loading) {
    return <p style={{ color: theme.colors.text.secondary }}>Loading project...</p>;
  }

  if (error || !project) {
    return (
      <Card>
        <Card.Body>
          <Stack gap="sm">
            <p style={{ color: theme.colors.semantic.error.default }}>{error || 'Project not found'}</p>
            <Button variant="outline" size="sm" onClick={onBack}>Back to Projects</Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  }

  const hasCredentials = project.has_credentials;

  return (
    <div>
      {/* Tab bar — content is always-mounted below with display:none for persistence */}
      <Tabs value={activeTab} onChange={setActiveTab}>
        <Tabs.List>
          {hasCredentials && <Tabs.Trigger value="graph">Graph</Tabs.Trigger>}
          {hasCredentials && <Tabs.Trigger value="audit">Audit Log</Tabs.Trigger>}
          <Tabs.Trigger value="members">Members</Tabs.Trigger>
          <Tabs.Trigger value="settings">Settings</Tabs.Trigger>
          <Tabs.Trigger value="instructions">Instructions</Tabs.Trigger>
        </Tabs.List>
      </Tabs>

      {/* Always-mounted tab panels — hidden via display:none to preserve state */}
      {hasCredentials && (
        <div ref={graphContainerRef} style={{
          display: activeTab === 'graph' ? 'flex' : 'none',
          flexDirection: 'column',
          marginTop: isFullscreen ? 0 : '8px',
          background: isFullscreen ? theme.colors.background.default : undefined,
          height: isFullscreen ? '100%' : undefined,
        }}>
          {exportError && (
            <div
              style={{
                padding: '6px 12px',
                fontSize: '0.85rem',
                color: theme.colors.semantic.error.default,
                background: theme.colors.background.default,
                borderBottom: `1px solid ${theme.colors.border.default}`,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span>{exportError}</span>
              <Button variant="ghost" size="sm" onClick={() => setExportError(null)}>Dismiss</Button>
            </div>
          )}
          <GraphToolbar
            projectId={projectId}
            nodes={graph.nodes}
            schema={graph.schema}
            onNodeCreated={graph.refetch}
            onEdgeCreated={graph.refetch}
            onExport={handleExport}
            colorBy={colorBy}
            onColorByChange={setColorBy}
            edgeFilterKey={edgeFilterKey}
            edgeFilterValue={edgeFilterValue}
            onEdgeFilterKeyChange={setEdgeFilterKey}
            onEdgeFilterValueChange={setEdgeFilterValue}
            edgePropertyKeys={edgePropertyKeys}
            lastUndoable={graph.lastUndoable}
            undoLoading={graph.undoLoading}
            onUndo={graph.handleUndo}
            onSearchNodes={handleSearchNodes}
            selectedNodeCount={selectedNodeCount}
            isFullscreen={isFullscreen}
            onToggleFullscreen={handleToggleFullscreen}
            onHighlight={(ids) => {
              const matchedNodes = graph.nodes.filter((n) => ids.includes(n.element_id));
              const matchedEdges = graph.edges.filter((e) => ids.includes(e.element_id));
              graph.handleQueryResult({
                nodes: matchedNodes, edges: matchedEdges,
                total_nodes: matchedNodes.length, total_edges: matchedEdges.length, truncated: false,
              });
            }}
            onClearHighlights={graph.clearQuery}
            edges={graph.edges}
            showAIExplorer={showAIExplorer}
            onToggleAIExplorer={() => {
              // Side-panel toggles no longer auto-clear the rendered cypher
              // subgraph — the user explicitly clears it via the toolbar's
              // Clear Cypher button (onClearCypherSubgraph). Panel toggles
              // just show/hide the side content.
              if (showAIExplorer) { setShowAIExplorer(false); graph.clearQuery(); }
              else { setShowAIExplorer(true); setShowCypherConsole(false); }
            }}
            showCypherConsole={showCypherConsole}
            onToggleCypherConsole={() => {
              if (showCypherConsole) { setShowCypherConsole(false); }
              else { setShowCypherConsole(true); setShowAIExplorer(false); setShowLayoutPanel(false); graph.clearQuery(); }
            }}
            showLayoutPanel={showLayoutPanel}
            onToggleLayoutPanel={() => {
              if (showLayoutPanel) {
                setShowLayoutPanel(false);
              } else {
                setShowLayoutPanel(true);
                setShowAIExplorer(false);
                setShowCypherConsole(false);
                graph.clearQuery();
              }
            }}
            hasCypherSubgraph={cypherSubgraphNodes.length > 0}
            onClearCypherSubgraph={handleClearCypherGraphData}
            layoutRunning={layoutRunning}
            onToggleLayoutRun={handleToggleLayoutRun}
          />
          <div style={{ display: 'flex', marginTop: '4px', flex: isFullscreen ? 1 : undefined, minHeight: 0 }}>
            {/* Cypher Console side panel — always mounted, hidden via display:none to retain state */}
            <div style={{
              display: showCypherConsole ? 'block' : 'none',
              width: '25%',
              minWidth: '300px',
              maxWidth: '450px',
              flexShrink: 0,
              overflowY: 'auto',
              borderRight: `1px solid ${theme.colors.border.default}`,
              height: isFullscreen ? '100%' : 'calc(100vh - 230px)',
              padding: '8px',
            }}>
              <CypherConsole
                projectId={projectId}
                schema={graph.schema}
                nodes={graph.nodes}
                onGraphData={handleCypherGraphData}
                onClearGraphData={handleClearCypherGraphData}
              />
            </div>
            {/* AI Explorer side panel — always mounted, hidden via display:none to retain state */}
            <div style={{
              display: showAIExplorer ? 'block' : 'none',
              width: '25%',
              minWidth: '300px',
              maxWidth: '450px',
              flexShrink: 0,
              overflowY: 'auto',
              borderRight: `1px solid ${theme.colors.border.default}`,
              height: isFullscreen ? '100%' : 'calc(100vh - 230px)',
            }}>
              <AIExplorer
                projectId={projectId}
                onHighlight={(ids) => {
                  const matchedNodes = graph.nodes.filter((n) => ids.includes(n.element_id));
                  const matchedEdges = graph.edges.filter((e) => ids.includes(e.element_id));
                  graph.handleQueryResult({
                    nodes: matchedNodes, edges: matchedEdges,
                    total_nodes: matchedNodes.length, total_edges: matchedEdges.length, truncated: false,
                  });
                }}
              />
            </div>
            {/* Layout Panel side panel — always mounted, hidden via display:none to retain state */}
            <div style={{
              display: showLayoutPanel ? 'block' : 'none',
              width: '25%',
              minWidth: '280px',
              maxWidth: '380px',
              flexShrink: 0,
              overflowY: 'auto',
              borderRight: `1px solid ${theme.colors.border.default}`,
              height: isFullscreen ? '100%' : 'calc(100vh - 230px)',
              padding: '8px',
            }}>
              <LayoutPanel
                engine={layoutEngine}
                onEngineChange={setLayoutEngine}
                fcose={fcoseParams}
                onFcoseChange={setFcoseParams}
                d3={d3Params}
                onD3Change={setD3Params}
                coseBilkent={coseBilkentParams}
                onCoseBilkentChange={setCoseBilkentParams}
                simple={simpleParams}
                onSimpleChange={setSimpleParams}
                onApply={handleApplyLayout}
                onReset={handleResetLayoutParams}
                onStop={handleStopLayout}
                running={layoutRunning}
              />
            </div>
            {/* Graph canvas */}
            <div style={{ position: 'relative', flex: 1, minWidth: 0, minHeight: 0 }}>
            {graph.loading ? (
              <p style={{ color: theme.colors.text.secondary, padding: '20px' }}>Loading graph...</p>
            ) : graph.error ? (
              <p style={{ color: theme.colors.semantic.error.default, padding: '20px' }}>{graph.error}</p>
            ) : graph.nodes.length === 0 ? (
              <Card>
                <Card.Body>
                  <p style={{ color: theme.colors.text.secondary, margin: 0 }}>
                    Graph is empty. Add nodes to get started.
                  </p>
                </Card.Body>
              </Card>
            ) : (
              <GraphCanvas
                ref={canvasRef}
                elements={cypherCytoscapeElements || graph.cytoscapeElements}
                highlightedIds={cypherCytoscapeElements ? new Set<string>() : graph.highlightedIds}
                colorBy={colorBy}
                onNodeSelect={handleNodeSelect}
                onEdgeSelect={handleEdgeSelect}
                edgeFilterKey={edgeFilterKey}
                edgeFilterValue={edgeFilterValue}
                onLayoutStart={() => setLayoutRunning(true)}
                onLayoutStop={() => setLayoutRunning(false)}
                style={isFullscreen ? { height: '100%' } : undefined}
              />
            )}
            {graph.truncated && (
              <p style={{ fontSize: '0.8rem', color: theme.colors.semantic.warning.default }}>
                Graph truncated to 1,000 nodes. Use queries to explore specific areas.
              </p>
            )}
            {/* Floating detail panel overlay */}
            {(graph.selectedNode || graph.selectedEdge) && (
              <div style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: detailPanelEditing ? 480 : 320,
                transition: 'width 0.2s ease',
                maxHeight: 'calc(100% - 24px)',
                overflowY: 'auto',
                zIndex: 10,
                background: `${theme.colors.background.elevated}f0`,
                borderRadius: '8px',
                boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                border: `1px solid ${theme.colors.border.default}`,
              }}>
                {graph.selectedNode && (
                  <NodeDetailPanel
                    key={graph.selectedNode.element_id}
                    projectId={projectId}
                    node={graph.selectedNode}
                    onUpdated={graph.refetch}
                    onClose={() => { graph.setSelectedNode(null); setDetailPanelEditing(false); }}
                    onEditingChange={setDetailPanelEditing}
                  />
                )}
                {graph.selectedEdge && (
                  <EdgeDetailPanel
                    key={graph.selectedEdge.element_id}
                    projectId={projectId}
                    edge={graph.selectedEdge}
                    nodes={graph.nodes}
                    onUpdated={graph.refetch}
                    onClose={() => { graph.setSelectedEdge(null); setDetailPanelEditing(false); }}
                    onEditingChange={setDetailPanelEditing}
                  />
                )}
              </div>
            )}
          </div>
          </div>
        </div>
      )}

      {hasCredentials && (
        <div style={{ display: activeTab === 'audit' ? 'block' : 'none', marginTop: '8px' }}>
          <AuditLog
            projectId={projectId}
            isAdmin={isAdmin}
            graphNodes={graph.nodes}
            graphEdges={graph.edges}
            projectName={project?.name || 'project'}
            onGraphRefetch={graph.refetch}
          />
        </div>
      )}

      <div style={{ display: activeTab === 'members' ? 'block' : 'none', marginTop: '8px' }}>
        <ProjectMembers projectId={projectId} />
      </div>

      <div style={{ display: activeTab === 'settings' ? 'block' : 'none', marginTop: '8px' }}>
        <ProjectSettings project={project} canEdit={canEdit} isAdmin={isAdmin} onUpdated={fetchProject} onDeleted={onBack} />
      </div>

      <div style={{ display: activeTab === 'instructions' ? 'block' : 'none', marginTop: '8px' }}>
        <InstructionsPanel />
      </div>

      <BackupWatermark projectId={projectId} />
    </div>
  );
};
